import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyRoleForm = {
  id: null,
  name: "",
  description: "",
  permissions: [],
};

function RolesPermissions({ permissions, roles, onReload }) {
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [savingRole, setSavingRole] = useState(false);

  const groupedPermissions = useMemo(() => {
    return permissions.reduce((groups, permission) => {
      const category = permission.category || "General";
      groups[category] = groups[category] || [];
      groups[category].push(permission);
      return groups;
    }, {});
  }, [permissions]);

  const handleRoleChange = (event) => {
    const { name, value } = event.target;
    setRoleForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePermission = (permissionId) => {
    setRoleForm((prev) => {
      const exists = prev.permissions.includes(permissionId);

      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((id) => id !== permissionId)
          : [...prev.permissions, permissionId],
      };
    });
  };

  const selectRole = (role) => {
    setRoleForm({
      id: role.id,
      name: role.name || "",
      description: role.description || "",
      permissions:
        role.permission_details?.map((permission) => permission.id) || [],
    });
  };

  const resetRoleForm = () => {
    setRoleForm(emptyRoleForm);
  };

  const saveRole = async (event) => {
    event.preventDefault();

    if (!roleForm.name.trim()) {
      toast.warning("Role name is required.");
      return;
    }

    try {
      setSavingRole(true);

      const payload = {
        name: roleForm.name,
        description: roleForm.description,
        permissions: roleForm.permissions,
      };

      if (roleForm.id) {
        await axiosInstance.put(`settings/roles/${roleForm.id}/`, payload);
        toast.success("Role updated successfully.");
      } else {
        await axiosInstance.post("settings/roles/", payload);
        toast.success("Role created successfully.");
      }

      resetRoleForm();
      onReload();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to save role.");
    } finally {
      setSavingRole(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;

    try {
      await axiosInstance.delete(`settings/roles/${role.id}/`);
      toast.success("Role deleted.");
      onReload();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to delete role.");
    }
  };

  return (
    <div className="row g-4">
      <div className="col-xl-5">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">{roleForm.id ? "Edit Role" : "Create Role"}</h5>
          </div>

          <div className="card-body">
            <form onSubmit={saveRole}>
              <div className="mb-3">
                <label className="form-label">Role Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={roleForm.name}
                  onChange={handleRoleChange}
                  placeholder="e.g. Deputy Head"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows="3"
                  value={roleForm.description}
                  onChange={handleRoleChange}
                ></textarea>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Permissions</label>
                  <small className="text-muted">
                    {roleForm.permissions.length} selected
                  </small>
                </div>

                <div
                  className="border rounded-3 p-3"
                  style={{ maxHeight: 420, overflowY: "auto" }}
                >
                  {Object.entries(groupedPermissions).map(([category, items]) => (
                    <div key={category} className="mb-3">
                      <h6 className="fw-bold mb-2">{category}</h6>

                      <div className="row g-2">
                        {items.map((permission) => (
                          <div className="col-md-6" key={permission.id}>
                            <label className="border rounded-3 p-2 d-flex gap-2 h-100">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={roleForm.permissions.includes(permission.id)}
                                onChange={() => togglePermission(permission.id)}
                              />

                              <span>
                                <span className="d-block fw-semibold small">
                                  {permission.name}
                                </span>
                                <span
                                  className="d-block text-muted"
                                  style={{ fontSize: 12 }}
                                >
                                  {permission.slug}
                                </span>
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={resetRoleForm}
                >
                  Clear
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={savingRole}
                >
                  {savingRole ? "Saving..." : roleForm.id ? "Update Role" : "Create Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-xl-7">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">Available Roles</h5>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Users</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id}>
                      <td>
                        <div className="fw-bold">{role.name}</div>
                        <small className="text-muted">{role.description}</small>

                        {role.is_system_role && (
                          <span className="badge text-bg-light border ms-2">
                            System
                          </span>
                        )}
                      </td>

                      <td>{role.permission_details?.length || 0}</td>
                      <td>{role.users_count || 0}</td>

                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-light border me-2"
                          onClick={() => selectRole(role)}
                          title="Edit role"
                        >
                          <i className="bi bi-pencil-square text-success"></i>
                        </button>

                        {!role.is_system_role && (
                          <button
                            className="btn btn-sm btn-light border"
                            onClick={() => deleteRole(role)}
                            title="Delete role"
                          >
                            <i className="bi bi-trash text-danger"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}

                  {roles.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No roles found yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RolesPermissions;
